import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { uploadFile } from 'actions/admin';
import { setShowSuccess } from 'actions/ui';
import SimpleAdmin, { DefaultOptions } from 'components/simpleAdmin';
import BlobImage from 'components/blobImage';
import { apiCode, apiConfig, apiGet, apiPatch, apiPost } from 'services';
import { Loading, Picker, SystemText } from 'e4shared';
import config from 'config';
import Upload from './components/upload';

export const AdminLanguages = ({ history }) => {
    const dispatch = useDispatch();
    const [data, setData] = React.useState([]);
    const [activeUsers, setActiveUsers] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [showPicker, setShowPicker] = React.useState(false);
    const [showUpload, setShowUpload] = React.useState('');
    const [pickerItem, setPickerItem] = React.useState({});
    const [fileErrors, setFileErrors] = React.useState([]);
    const [file, setFile] = React.useState(null);
    const [uploadLoading, setUploadLoading] = React.useState(false);

    const user = useSelector(state => state.user.user);
    const company = user.companySettings;
    if (!user.sys_Config) history.replace('/');

    const api = {
        load: () => {
            setLoading(true);
            apiGet('language', user.guid, response => {
                setData(response.map(x => ({ ...x, activeDisabled: isDisabled(response.length, x) })));
                setLoading(false);
            });
        },
        save: company => {
            apiPatch('language', company, user.guid, () => api.load());
        },
        download: code => {
            fetch(`${config.apiGateway.URL}language-file/${code}${apiCode(true)}`, apiConfig(user.guid, null, true))
                .then(response => {
                    const blob = response.blob();
                    blob.type = response.headers['content-type'];
                    return blob;
                })
                .then(blob => {
                    setLoading(false);
                    let now = new Date();
                    let a = document.createElement('a');
                    document.body.appendChild(a);
                    let url = window.URL.createObjectURL(blob);
                    a.style = 'display: none';
                    a.href = url;
                    a.download = `richardson-insights-translation-${code.toLowerCase()}-${now.toISOString()}.xlsx`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                })
                .catch(err => {
                    // console.log(err);
                });
        },
        saveStaff: (language, callback) => {
            apiPatch('language-staff', language, user.guid, () => {
                callback();
                api.load();
            });
        },
    };

    React.useEffect(() => {
        apiGet('profile', user.guid, response => {
            setActiveUsers(response.filter(p => p.active));
        });

        api.load();
    }, []);

    React.useEffect(() => {
        if (file) {
            file.companyId = user.company_Id;
            let errs = [];
            setFileErrors([]);
            if (file.name.indexOf('.xlsx') !== file.name.length - 5) {
                errs = [SystemText('Admin/Languages/Upload/Errors/InvalidFileType')];
                setFileErrors(errs);
            } else if (file.name.toLowerCase().indexOf(showUpload.toLowerCase()) < 0) {
                errs = [SystemText('Admin/Languages/Upload/Errors/InvalidLanguage', showUpload)];
                setFileErrors(errs);
            } else {
                setUploadLoading(true);
                const packet = {};
                const path = 'reports:translation:upload';
                packet[path] = file;
                uploadFile(packet, path, config, apiConfig(user.guid, true), response => {
                    setTimeout(() => {
                        apiGet(
                            `language-upload/${showUpload}?report=${response?.data?.path}`,
                            user.guid,
                            response => {
                                setUploadLoading(false);

                                // response is a list of errors. In case it's 0, it means no error
                                if (response?.length === 0) {
                                    setShowUpload('');
                                    dispatch(setShowSuccess(SystemText('Admin/Languages/Upload/Success')));
                                    return;
                                }

                                // errors returned from API, so display it
                                setFileErrors(response.map(x => x.item1));
                            },
                            () => {
                                setUploadLoading(false);
                                setFileErrors([SystemText('Admin/Languages/Upload/Errors/Unknown')]);
                            }
                        );
                    }, 1000);
                });
            }
        }
    }, [file]);

    const isActive = l => {
        const company = user.companySettings;
        return data.length <= 1 || (company.language_Id || 1) === l.id || (company.language_Ids || []).some(x => x === l.id);
    };

    const isDefault = l => {
        const company = user.companySettings;
        return data.length <= 1 || (company.language_Id || 1) === l.id;
    };

    const isDisabled = (len, l) => {
        return len <= 1 || (company.language_Id || 1) === l.id;
    };

    const setLanguageDefault = id => {
        const company = user.companySettings;
        company.language_Id = id;
        api.save(company);
    };

    const setLanguageActive = language => {
        const { id, active, activeDisabled } = language;
        if (!activeDisabled) {
            const company = user.companySettings;
            company.language_Ids = (company.language_Ids || []).filter(x => x !== id);
            if (active) company.language_Ids = company.language_Ids.concat([id]);
            api.save(company);
        }
    };

    const options = {
        ...DefaultOptions(),
        label: SystemText('Admin/Languages/Title'),
        labelPlural: SystemText('Admin/Languages/Title/Plural'),
        showDelete: false,
        showActive: true,
        extraOptions: [
            {
                btnClass: d => (isDefault(d) ? 'btn-success' : 'btn-danger'),
                label: d =>
                    isDefault(d)
                        ? SystemText('Admin/Languages/Table/Options/Default')
                        : SystemText('Admin/Languages/Table/Options/NonDefault'),
                icon: 'fa-language',
                disabled: d => isDefault(d) || !isActive(d),
                onClick: d => setLanguageDefault(d.id),
            },
            {
                btnClass: 'btn-info',
                label: SystemText('Admin/Languages/Table/Options/Download'),
                icon: 'fa-cloud-download',
                onClick: d => api.download(d.code),
            },
            {
                btnClass: 'btn-info',
                label: SystemText('Admin/Languages/Table/Options/Upload'),
                icon: 'fa-cloud-upload',
                onClick: d => setShowUpload(d.code),
            },
        ],
    };

    if (!user.support) {
        options.extraOptions = options.extraOptions.filter(x => x.icon === 'fa-language');
    }

    const tableColumns = [
        {
            label: SystemText('Admin/Languages/Table/Columns/Language'),
            dataProperty: 'name',
            renderer: l => <span>{l.name}</span>,
        },
        {
            label: SystemText('Admin/Languages/Table/Columns/Users'),
            dataProperty: 'staff_Ids',
            showCount: true,
            onClick: item => {
                setShowPicker(true);
                setPickerItem(item);
            },
        },
    ];

    return (
        <div>
            {loading ? (
                <Loading />
            ) : (
                <SimpleAdmin
                    blockAddNew
                    update={setLanguageActive}
                    data={data}
                    fields={[]}
                    options={options}
                    columns={tableColumns}
                />
            )}
            <Picker
                title={SystemText('Admin/Languages/Table/Columns/Users/Picker/Title', pickerItem?.name)}
                help={SystemText('Admin/Languages/Table/Columns/Users/Picker/Help')}
                entity="Users"
                data={activeUsers}
                nameProperty="full_Name"
                filterOption="staff"
                value={pickerItem?.staff_Ids}
                show={showPicker}
                onSave={ids => {
                    api.saveStaff({ ...pickerItem, update_Staff_Ids: ids }, () => {
                        dispatch(setShowSuccess(SystemText('Admin/Languages/Success/Users')));
                        setShowPicker(false);
                        api.load();
                    });
                }}
                onClose={() => {
                    setPickerItem({});
                    setShowPicker(false);
                }}
                itemPrefix={p => (
                    <div className="image-holder mr10">
                        <BlobImage folder="avatar" src={p.avatar} initials={p.initials} />
                    </div>
                )}
            />

            <Upload
                show={showUpload}
                onClose={() => {
                    setShowUpload('');
                    setFileErrors([]);
                }}
                file={file}
                setFile={setFile}
                loading={uploadLoading}
                fileErrors={fileErrors}
            />
        </div>
    );
};

export default AdminLanguages;
